import { useCallback, useEffect, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { marketConfigService, providerService } from "@/services/providerService";
import { errorMessage } from "@/services/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ADMIN_NAV } from "./nav";

const field = "h-11 rounded-md border-[#22252F] bg-[#0F1116] text-white focus-visible:ring-[#2563EB]";
const labelCls = "text-xs uppercase tracking-[0.2em] text-[#94A3B8]";

export default function AdminMarketConfigPage() {
  const [markets, setMarkets] = useState([]);
  const [config, setConfig] = useState([]);
  const [limits, setLimits] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", market: "crypto", asset_limit: 50 });

  const load = useCallback(async () => {
    try {
      const [cfg, mk] = await Promise.all([marketConfigService.list(), providerService.markets()]);
      setConfig(cfg);
      setMarkets(mk);
      const next = {};
      cfg.forEach((m) => m.categories.forEach((c) => (next[c.id] = c.asset_limit)));
      setLimits(next);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveLimit = async (c) => {
    try {
      await marketConfigService.update(c.id, { asset_limit: Number(limits[c.id]) });
      toast.success(`${c.name}: limite atualizado`);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await marketConfigService.create({ ...form, asset_limit: Number(form.asset_limit) || 0 });
      toast.success("Categoria criada");
      setOpen(false);
      setForm({ name: "", market: "crypto", asset_limit: 50 });
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remover a categoria ${c.name}?`)) return;
    try {
      await marketConfigService.remove(c.id);
      toast.success("Categoria removida");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Configuração de Mercados" subtitle="Limites de ativos por categoria e provedores atribuídos">
      <div data-testid="admin-market-config-page" className="space-y-6">
        <div className="flex justify-end">
          <Button data-testid="create-category-button" onClick={() => setOpen(true)}
                  className="h-11 rounded-md bg-[#2563EB] text-xs font-semibold uppercase tracking-[0.15em] transition-colors hover:bg-[#1D4ED8]">
            <Plus className="mr-2 h-4 w-4" /> Nova categoria
          </Button>
        </div>

        {config.map((m) => {
          const marketProviders = markets.find((x) => x.slug === m.market)?.providers || [];
          return (
            <section key={m.market} data-testid={`market-block-${m.market}`}
                     className="rounded-md border border-[#22252F] bg-[#12141A] p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <h2 className="font-heading text-lg font-light tracking-tight text-white md:text-lg">{m.label}</h2>
                <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#60A5FA]">
                  {marketProviders.map((p) => (
                    <span key={p.slug} className="border border-[#22252F] px-2 py-1" title={p.role}>
                      {p.name} · {p.refresh_seconds}s
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {m.categories.length === 0 ? (
                  <p className="text-sm text-[#64748B]">Sem categorias definidas</p>
                ) : null}
                {m.categories.map((c) => (
                  <div key={c.id} data-testid={`category-row-${c.slug}`}
                       className="flex flex-wrap items-center justify-between gap-4 border-t border-[#1A1D25] pt-3">
                    <div className="min-w-0">
                      <p className="text-sm text-white">{c.name}</p>
                      <p className="font-mono text-[11px] text-[#94A3B8]">
                        {c.slug} · {c.assets_count ?? 0} ativos
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className={labelCls}>Limite</Label>
                      <Input type="number" min="0" data-testid={`limit-input-${c.slug}`}
                             value={limits[c.id] ?? c.asset_limit}
                             onChange={(e) => setLimits({ ...limits, [c.id]: e.target.value })}
                             className={`${field} w-28 font-mono`} />
                      <button onClick={() => saveLimit(c)} data-testid={`save-limit-${c.slug}`}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#10B981]/50 hover:text-[#10B981]">
                        <Check className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(c)} data-testid={`delete-category-${c.slug}`}
                              className="rounded-md border border-[#22252F] p-2 text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#22252F] bg-[#12141A] text-white" data-testid="create-category-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light tracking-tight">Nova categoria</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-2"><Label className={labelCls}>Nome</Label>
              <Input required data-testid="new-category-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Mercado</Label>
                <select data-testid="new-category-market" value={form.market}
                        onChange={(e) => setForm({ ...form, market: e.target.value })}
                        className="h-11 w-full rounded-md border border-[#22252F] bg-[#0F1116] px-3 text-sm text-white">
                  {["crypto", "stocks", "forex", "commodities", "indices", "b3"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select></div>
              <div className="space-y-2"><Label className={labelCls}>Limite de ativos</Label>
                <Input type="number" min="0" data-testid="new-category-limit" value={form.asset_limit}
                       onChange={(e) => setForm({ ...form, asset_limit: e.target.value })} className={`${field} font-mono`} /></div>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="submit-new-category" className="rounded-md bg-[#2563EB] hover:bg-[#1D4ED8]">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
